import React, {
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";

import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

import {
  AddressColumnTitle,
  AddressFieldActionControl,
  AddressFieldEditorDialog,
  addressValueInputSx,
} from "../../../../common/Addresses/AddressFields";

const ui = {
  panel: "#ffffff",
  border: "#e6e9ee",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
};

const COLUMN_META = {
  deliveryAddress: {
    Icon: LocalShippingOutlinedIcon,
  },
  postalAddress: {
    Icon: MailOutlineRoundedIcon,
  },
  invoiceAddress: {
    Icon: ReceiptLongOutlinedIcon,
  },
  visitingAddress: {
    Icon: PlaceOutlinedIcon,
  },
};

const AddressFieldRow = memo(
  ({
    addressName,
    fieldName,
    field,
    value,
    index,
    onValueChange,
    onEditField,
  }) => {
    const hidden = field.type === "HIDDEN";

    const handleChange = useCallback(
      event =>
        onValueChange(
          addressName,
          fieldName,
          event.target.value
        ),
      [
        onValueChange,
        addressName,
        fieldName,
      ]
    );

    const handleEdit = useCallback(
      () => onEditField(fieldName),
      [
        onEditField,
        fieldName,
      ]
    );

    return (
      <Draggable
        draggableId={`${addressName}-${fieldName}`}
        index={index}
      >
        {(dragProvided, snapshot) => (
          <Box
            ref={dragProvided.innerRef}
            {...dragProvided.draggableProps}
            sx={{
              mb: 1,
              opacity: snapshot.isDragging
                ? 0.58
                : hidden
                  ? 0.72
                  : 1,
              transition: "opacity 120ms ease",
            }}
          >
            <Stack
              direction="row"
              spacing={0.38}
              alignItems="center"
            >
              <Tooltip title="Dra för att ändra ordning">
                <Box
                  {...dragProvided.dragHandleProps}
                  sx={{
                    width: 18,
                    height: 36,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 0.8,
                    color: ui.subtle,
                    cursor: "grab",
                    "&:hover": {
                      bgcolor: ui.soft,
                      color: ui.text,
                    },
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                >
                  <DragIndicatorRoundedIcon
                    sx={{ fontSize: 14 }}
                  />
                </Box>
              </Tooltip>

              <TextField
                fullWidth
                label={field.label || fieldName}
                value={value}
                onChange={handleChange}
                sx={{
                  ...addressValueInputSx,
                  "& .MuiOutlinedInput-root": {
                    ...addressValueInputSx[
                    "& .MuiOutlinedInput-root"
                    ],
                    bgcolor: hidden
                      ? ui.soft
                      : "#fff",
                  },
                }}
              />

              <Box sx={{ width: 52, flexShrink: 0 }}>
                <AddressFieldActionControl
                  value={field.type || "OPTIONAL"}
                  onEdit={handleEdit}
                />
              </Box>
            </Stack>
          </Box>
        )}
      </Draggable>
    );
  }
);

AddressFieldRow.displayName = "AddressFieldRow";

AddressFieldRow.propTypes = {
  addressName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  field: PropTypes.shape({
    displayOrder: PropTypes.number.isRequired,
    label: PropTypes.string,
    type: PropTypes.string.isRequired,
  }).isRequired,
  value: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onEditField: PropTypes.func.isRequired,
};

const AddressFields = ({
  fields,
  label,
  name,
  fieldValues,
  handleFieldConfigSave,
  handleValueChange,
  onDragEnd,
}) => {
  const [editingFieldName, setEditingFieldName] =
    useState(null);

  const sortedFields = useMemo(
    () =>
      Object.entries(fields || {}).sort(
        ([, a], [, b]) =>
          a.displayOrder - b.displayOrder
      ),
    [fields]
  );

  const hiddenCount = useMemo(
    () =>
      sortedFields.filter(
        ([, field]) => field.type === "HIDDEN"
      ).length,
    [sortedFields]
  );

  const meta =
    COLUMN_META[name] || COLUMN_META.deliveryAddress;

  const ColumnIcon = meta.Icon;

  const editingField = editingFieldName
    ? fields?.[editingFieldName]
    : null;

  const handleDragEnd = useCallback(
    result => onDragEnd(name, result),
    [onDragEnd, name]
  );

  const handleEditField = useCallback(
    fieldName => setEditingFieldName(fieldName),
    []
  );

  const handleCloseEditor = useCallback(
    () => setEditingFieldName(null),
    []
  );

  const handleSaveEditor = useCallback(
    config => {
      if (!editingFieldName) {
        return;
      }

      handleFieldConfigSave(
        name,
        editingFieldName,
        config
      );

      setEditingFieldName(null);
    },
    [
      handleFieldConfigSave,
      name,
      editingFieldName,
    ]
  );

  return (
    <Grid item xs={12} md={6} xl={3} sx={{ display: "flex" }}>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: ui.panel,
          border: `1px solid ${ui.border}`,
          borderRadius: 1.35,
          boxShadow:
            "0 1px 2px rgba(16,24,40,.025), 0 8px 22px rgba(16,24,40,.018)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            minHeight: 50,
            px: 1.05,
            py: 0.8,
            display: "flex",
            alignItems: "center",
            borderBottom: `1px solid ${ui.border}`,
          }}
        >
          <AddressColumnTitle
            title={label}
            icon={<ColumnIcon sx={{ fontSize: 15 }} />}
          />

          {hiddenCount > 0 && (
            <Typography
              sx={{
                ml: 0.6,
                flexShrink: 0,
                color: ui.subtle,
                fontSize: 8.7,
                lineHeight: 1.2,
              }}
            >
              Dolda fält ({hiddenCount})
            </Typography>
          )}
        </Box>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`address-fields-${name}`}>
            {provided => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  p: 0.75,
                  flex: 1,
                }}
              >
                {sortedFields.map(
                  ([fieldName, field], index) => (
                    <AddressFieldRow
                      key={fieldName}
                      addressName={name}
                      fieldName={fieldName}
                      field={field}
                      value={fieldValues?.[fieldName] ?? ""}
                      index={index}
                      onValueChange={handleValueChange}
                      onEditField={handleEditField}
                    />
                  )
                )}

                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <AddressFieldEditorDialog
          open={Boolean(editingField)}
          fieldLabel={
            editingField?.label || editingFieldName || ""
          }
          fieldType={editingField?.type || "OPTIONAL"}
          onClose={handleCloseEditor}
          onSave={handleSaveEditor}
        />
      </Box>
    </Grid>
  );
};

AddressFields.defaultProps = {
  fieldValues: {},
};

AddressFields.propTypes = {
  fields: PropTypes.objectOf(
    PropTypes.shape({
      displayOrder: PropTypes.number.isRequired,
      label: PropTypes.string,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,

  label: PropTypes.node.isRequired,

  name: PropTypes.string.isRequired,

  fieldValues: PropTypes.object,

  handleFieldConfigSave: PropTypes.func.isRequired,

  handleValueChange: PropTypes.func.isRequired,

  onDragEnd: PropTypes.func.isRequired,
};

export default memo(AddressFields);